import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { parseListResult } from "@/lib/listResult";
import { useToast } from "@/hooks/use-toast";

export interface ListFilters {
  search: string;
  status: string;
  region?: string;
  source?: string;
  fromDate?: string;
  toDate?: string;
  sortDir: "desc" | "asc";
}

export interface ListState<T> {
  rows: T[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  filters: ListFilters;
  setFilters: (f: Partial<ListFilters>) => void;
  resetFilters: () => void;
  loadMore: () => void;
  refresh: () => Promise<void>;
  error: string | null;
}

type RpcName = keyof Database["public"]["Functions"];

interface UseListQueryOptions {
  rpcName: RpcName;
  scope: string;
  pageSize?: number;
  extraParams?: Record<string, any>;
  debounce?: number;
  /** Initial filters to apply (e.g. from a default saved view) */
  initialFilters?: Partial<ListFilters>;
}

const DEFAULT_FILTERS: ListFilters = {
  search: "",
  status: "all",
  sortDir: "desc",
};

export function useListQuery<T = any>(options: UseListQueryOptions): ListState<T> {
  const { rpcName, pageSize = 25, extraParams, debounce = 300, initialFilters } = options;
  const { toast } = useToast();
  // Stabilize extraParams to avoid infinite re-render loops
  const extraParamsJson = JSON.stringify(extraParams ?? {});
  const stableExtraParams = useMemo(() => JSON.parse(extraParamsJson), [extraParamsJson]);
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursorCreatedAt, setNextCursorCreatedAt] = useState<string | null>(null);
  const [nextCursorId, setNextCursorId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFiltersRaw] = useState<ListFilters>({
    ...DEFAULT_FILTERS,
    ...initialFilters,
  });

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const setFilters = useCallback((partial: Partial<ListFilters>) => {
    setFiltersRaw((prev) => ({ ...prev, ...partial }));
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersRaw({ ...DEFAULT_FILTERS });
  }, []);

  const fetchData = useCallback(
    async (cursorCreatedAt?: string, cursorId?: string) => {
      const f = filtersRef.current;
      const params: Record<string, any> = {
        p_search: f.search,
        p_status: f.status,
        p_sort_dir: f.sortDir,
        p_page_size: pageSize,
        ...stableExtraParams,
      };
      if (f.region && f.region !== "all") params.p_region = f.region;
      if (f.source && f.source !== "all") params.p_source = f.source;
      if (f.fromDate) params.p_from_date = f.fromDate;
      if (f.toDate) params.p_to_date = f.toDate;
      if (cursorCreatedAt) params.p_cursor_created_at = cursorCreatedAt;
      if (cursorId) params.p_cursor_id = cursorId;

      // RPC functions with dynamic params — the rpcName is typed as a valid function name
      // but the params shape varies per function so we need the cast on params only
      const { data, error } = await supabase.rpc(rpcName, params as any);

      if (error) {
        console.error(`RPC ${rpcName} error:`, error);
        throw new Error(error.message || `Could not load ${options.scope}.`);
      }
      return parseListResult<T>(data);
    },
    [rpcName, pageSize, stableExtraParams, options.scope]
  );

  const applyResult = useCallback((result: { rows: T[]; next_cursor_created_at: string | null; next_cursor_id: string | null }) => {
    setRows(result.rows ?? []);
    setNextCursorCreatedAt(result.next_cursor_created_at);
    setNextCursorId(result.next_cursor_id);
    setError(null);
  }, []);

  // Initial + filter-change fetch (debounced for search)
  // Keep previous rows visible during loading to prevent blank flash
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const result = await fetchData();
        applyResult(result);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Could not load records.";
        setError(message);
        toast({ title: "Could not load records", description: message, variant: "destructive" });
      } finally {
        setLoading(false);
      }
    }, filters.search ? debounce : 0);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [filters.search, filters.status, filters.region, filters.source, filters.fromDate, filters.toDate, filters.sortDir, fetchData, applyResult, debounce, toast]);

  const loadMore = useCallback(async () => {
    if (!nextCursorCreatedAt || loadingMore) return;
    setLoadingMore(true);
    try {
      const result = await fetchData(nextCursorCreatedAt, nextCursorId ?? undefined);
      setRows((prev) => [...prev, ...(result.rows ?? [])]);
      setNextCursorCreatedAt(result.next_cursor_created_at);
      setNextCursorId(result.next_cursor_id);
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not load more records.";
      setError(message);
      toast({ title: "Could not load more records", description: message, variant: "destructive" });
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursorCreatedAt, nextCursorId, loadingMore, fetchData, toast]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchData();
      applyResult(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not refresh records.";
      setError(message);
      toast({ title: "Could not refresh records", description: message, variant: "destructive" });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchData, applyResult, toast]);

  return {
    rows,
    loading,
    loadingMore,
    hasMore: !!nextCursorCreatedAt,
    filters,
    setFilters,
    resetFilters,
    loadMore,
    refresh,
    error,
  };
}
